import { RootState } from '@/redux/reducers';
import { Roles } from '@/common/Roles';
import { InclusionPath } from './types';

interface CurrentUserLike {
  roles?: string[];
}

interface SchoolLike {
  insuranceForForeign?: boolean;
}

/**
 * Calcule l'aiguillage free vs paid en fonction du rôle utilisateur,
 * du paramétrage école, et de la réponse à la question sécu sociale.
 *
 * Règles :
 * - non inclusion                                                  → paid
 * - inclusion + insuranceForForeign=true                           → free
 * - inclusion + insuranceForForeign=false + hasFrenchSocialSecu    → free
 * - inclusion + insuranceForForeign=false + !hasFrenchSocialSecu   → paid
 *
 * Tant qu'on ne sait pas (pas de user / réponse sécu absente),
 * on renvoie 'paid' par défaut (le plus restrictif).
 */
export const computeInclusionPath = (
  currentUser: CurrentUserLike | null | undefined,
  school: SchoolLike | null | undefined,
  hasFrenchSocialSecurity: boolean | null,
): InclusionPath => {
  const isInclusion = !!currentUser?.roles?.some(
    role => role === Roles.STUDENT_INCLUSIVE.value,
  );
  if (!isInclusion) return 'paid';

  if (school?.insuranceForForeign === true) return 'free';
  if (hasFrenchSocialSecurity === true) return 'free';
  return 'paid';
};

export const selectInclusionPath = (state: RootState): InclusionPath => {
  const { hasFrenchSocialSecurity } = state.santeMobilite;
  const currentUser = state.auth?.data?.user as CurrentUserLike | undefined;
  const school = state.school?.school as SchoolLike | undefined;
  return computeInclusionPath(currentUser, school, hasFrenchSocialSecurity);
};

/**
 * L'utilisateur est-il un étudiant inclusion ?
 *
 * Distinct de `selectInclusionPath`, qui répond « ce parcours est-il gratuit ».
 * La nuance compte avant l'étape Sécu : on sait déjà que l'étudiant est en
 * inclusion, mais pas encore si son parcours sera gratuit — `computeInclusionPath`
 * retombe alors sur 'paid' par prudence. Afficher le tarif payant à ce
 * moment-là annonce à un étudiant dont l'école paie un prix qu'il ne paiera
 * jamais, puis le fait disparaître deux écrans plus loin.
 */
export const selectIsInclusionStudent = (state: RootState): boolean => {
  const currentUser = state.auth?.data?.user as CurrentUserLike | undefined;
  return !!currentUser?.roles?.some(
    role => role === Roles.STUDENT_INCLUSIVE.value,
  );
};
